import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { usePlaidLink } from 'react-plaid-link'
import clsx from 'clsx'
import { useCompletePlaidLink, useGetPlaidLinkToken } from '../../../api/plaid'
import { useAppContext } from '../../../redux/slices/appContext'
import SydButton from '../../commonDesign/Button'

/**
 * PlaidLink - Wrapper component for Plaid Link integration
 *
 * Handles the full Plaid Link flow:
 * 1. Fetches a link token from the Orion Plaid API
 * 2. Opens Plaid Link for the user to connect a bank account
 * 3. On success, calls plaid-complete-link which exchanges the token,
 *    creates shell accounts, and stores the integration record
 *
 * @component
 * @example
 * <PlaidLink
 *   onSuccess={(result) => console.log('Linked!', result.createdAccounts)}
 *   onExit={(error) => console.log('Exited', error)}
 *   buttonText="Connect Bank Account"
 * />
 */
const PlaidLink = ({
  onSuccess,
  onExit,
  onEvent,
  loadingText = 'Initializing...',
  processingText = 'Linking Accounts...',
  buttonText = 'Link Accounts',
  buttonProps,
  classNames = {
    root: 'flex flex-col items-center gap-4',
    error: 'text-red-600',
    button: ''
  },
  autoOpen = false,
  disabled = false,
  linkParams = { appName: 'Summit Wealth', isInform: false }
}) => {
  const { clientId } = useAppContext()
  const [isCompleting, setIsCompleting] = useState(false)
  const [completionError, setCompletionError] = useState(null)
  const [lastLinkResult, setLastLinkResult] = useState(null)

  // 1. Fetch the Plaid Link token
  const {
    data: tokenData,
    isLoading: isLoadingToken,
    error: tokenError
  } = useGetPlaidLinkToken({
    clientId,
    ...linkParams
  }, {
    enabled: !disabled && !!clientId
  })

  const linkToken = useMemo(() => tokenData?.data?.link_token, [tokenData])

  // 2. Mutation to complete the full link flow on the backend
  const completeLink = useCompletePlaidLink({ clientId })

  const executeCompleteLink = useCallback(async (publicToken, institution) => {
    setIsCompleting(true)
    setCompletionError(null)
    try {
      const result = await completeLink.mutateAsync({
        publicToken,
        institution
      })
      setLastLinkResult(null)
      if (onSuccess) {
        onSuccess(result)
      }
    } catch (err) {
      console.error('Failed to complete Plaid link:', err)
      setCompletionError(err)
      setLastLinkResult({ publicToken, institution })
    } finally {
      setIsCompleting(false)
    }
  }, [onSuccess, completeLink])

  // Handle successful link
  const handleOnSuccess = useCallback(async (publicToken, metadata) => {
    await executeCompleteLink(publicToken, metadata.institution)
  }, [executeCompleteLink])

  const handleRetry = useCallback(async () => {
    if (lastLinkResult) {
      await executeCompleteLink(lastLinkResult.publicToken, lastLinkResult.institution)
    }
  }, [lastLinkResult, executeCompleteLink])

  // Handle exit
  const handleOnExit = useCallback((error, metadata) => {
    if (onExit) {
      onExit(error, metadata)
    }
  }, [onExit])

  // Handle events
  const handleOnEvent = useCallback((eventName, metadata) => {
    if (onEvent) {
      onEvent(eventName, metadata)
    }
  }, [onEvent])

  // Configure Plaid Link
  const { open, ready, error: plaidError } = usePlaidLink({
    token: linkToken || null,
    onSuccess: handleOnSuccess,
    onExit: handleOnExit,
    onEvent: handleOnEvent
  })

  // Auto-open if requested and ready
  useEffect(() => {
    if (autoOpen && ready && open) {
      open()
    }
  }, [autoOpen, ready, open])

  if (isLoadingToken || isCompleting) {
    return (
      <div className={classNames.root}>
        <SydButton processing disabled {...buttonProps}>
          {isCompleting ? processingText : loadingText}
        </SydButton>
      </div>
    )
  }

  if (completionError) {
    return (
      <div className={classNames.root}>
        <div className={classNames.error}>
          Something went wrong while setting up your accounts.
        </div>
        <SydButton onClick={handleRetry} {...buttonProps}>
          Retry
        </SydButton>
      </div>
    )
  }

  // Handle token error
  if (tokenError || plaidError || !linkToken) {
    return (
      <div className={clsx(classNames.root, classNames.error)}>
        Failed to load Plaid Link. Try again.
      </div>
    )
  }

  return (
    <div className={classNames.root}>
      <SydButton
        onClick={open}
        disabled={!ready || disabled}
        className={classNames.button}
        {...buttonProps}
      >
        {!ready ? loadingText : buttonText}
      </SydButton>
    </div>
  )
}

PlaidLink.propTypes = {
  /** Callback with (result, metadata) - result has { itemID, createdAccounts, integration } */
  onSuccess: PropTypes.func.isRequired,
  /** Callback when user exits Link or completion fails */
  onExit: PropTypes.func,
  /** Callback for Plaid Link events */
  onEvent: PropTypes.func,
  /** Text to display on the button */
  buttonText: PropTypes.string,
  /** Text to display while loading the link token */
  loadingText: PropTypes.string,
  /** Text to display while completing the link on the backend */
  processingText: PropTypes.string,
  /** Additional props to pass to the button */
  buttonProps: PropTypes.object,
  /** Whether to automatically open Link when ready */
  autoOpen: PropTypes.bool,
  /** Whether the component is disabled */
  disabled: PropTypes.bool,
  /** Class names for styling */
  classNames: PropTypes.shape({
    root: PropTypes.string,
    error: PropTypes.string,
    button: PropTypes.string
  }),
  linkParams: PropTypes.shape({
    appName: PropTypes.string,
    isInform: PropTypes.bool
  })
}

export default PlaidLink
